import { useId, useRef, type ReactElement } from 'react';
import { Button } from '../details/Button';
import { useFocusRescue } from '../details/use-focus-rescue';
import { ConnectionSection } from './ConnectionSection';
import { adminErrorMessage } from './error-message';
import { LinesSection } from './LinesSection';
import { SectionHeading } from './SectionHeading';
import { SimulationSection } from './SimulationSection';
import { SyncSection } from './SyncSection';
import { WebhookSection } from './WebhookSection';
import { useConnection } from './use-connection';
import section from './Section.module.css';

/**
 * WhatsApp: a conexão (credenciais que o provedor da instalação pede), os números, a importação de
 * histórico (só onde a instalação importa) e, na demonstração, o painel de simulação.
 */
export function WhatsAppScreen(): ReactElement {
  const headingId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const connection = useConnection();

  // "Tentar de novo" some quando a conexão chega e levaria o foco ao <body>.
  const rescue = useFocusRescue(!connection.isError, headingRef);
  const retry = (): void => {
    rescue.arm();
    void connection.refetch().then((result) => {
      if (result.isError) rescue.disarm();
    });
  };

  const data = connection.data;

  return (
    <section aria-labelledby={headingId} className={section.screen}>
      <SectionHeading id={headingId} ref={headingRef}>
        WhatsApp
      </SectionHeading>
      <p className={section.lead}>
        A conexão com o WhatsApp e os números que a equipe usa para atender. Sem conexão, o CladvApp
        não recebe nem envia mensagens.
      </p>

      {connection.isPending && (
        <p role="status" className={section.loading}>
          Carregando a conexão…
        </p>
      )}

      {connection.isError && data === undefined && (
        <div className={section.loadError}>
          <p role="alert" className={section.loadErrorText}>
            Não foi possível carregar a conexão do WhatsApp.{' '}
            {adminErrorMessage(connection.error, 'Tente de novo.')}
          </p>
          <Button busy={connection.isFetching} onClick={retry}>
            Tentar de novo
          </Button>
        </div>
      )}

      {data !== undefined && (
        <>
          <ConnectionSection connection={data} />
          {data.webhookPath && (
            <WebhookSection path={data.webhookPath} canSubscribe={data.canSubscribe} />
          )}
          <LinesSection connection={data} />
          {data.canSync && <SyncSection />}
          {data.simulation && <SimulationSection connection={data} />}
        </>
      )}
    </section>
  );
}
